/**
 * useCardanoWallet Hook
 *
 * Custom React hook for managing Cardano wallet data and state
 */

import { useQuery } from '@tanstack/react-query';
import { cardanoAPI, lovelaceToAda } from '../services/cardanoApi';
import type { CardanoWalletData } from '../types/cardano.types';

/**
 * Fetch ADA price
 */
export const useAdaPrice = () => {
  return useQuery({
    queryKey: ['cardano', 'price'],
    queryFn: () => cardanoAPI.getAdaPrice(),
    staleTime: 60_000,
    refetchInterval: 2 * 60_000,
  });
};

/**
 * Fetch wallet data for multiple addresses
 */
export const useCardanoWallets = (addresses: string[]) => {
  const { data: adaPrice = 0 } = useAdaPrice();

  const {
    data: wallets,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['cardano', 'wallets', addresses, adaPrice],
    queryFn: () => cardanoAPI.getMultipleWalletsData(addresses, adaPrice),
    enabled: addresses.length > 0 && adaPrice > 0,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  // Calculate aggregated metrics
  const totalBalance = wallets?.reduce((sum, w) => sum + w.balance, 0) || 0;
  const totalBalanceUsd = wallets?.reduce((sum, w) => sum + w.balanceUsd, 0) || 0;
  const totalStaked = wallets?.reduce((sum, w) => sum + w.stakedAmount, 0) || 0;
  const totalStakedUsd = wallets?.reduce((sum, w) => sum + w.stakedAmountUsd, 0) || 0;
  const totalWithdrawable = wallets?.reduce((sum, w) => sum + w.withdrawableRewards, 0) || 0;
  const totalWithdrawableUsd = wallets?.reduce((sum, w) => sum + w.withdrawableRewardsUsd, 0) || 0;
  const totalRewards = wallets?.reduce((sum, w) => sum + w.totalRewards, 0) || 0;
  const totalRewardsUsd = wallets?.reduce((sum, w) => sum + w.totalRewardsUsd, 0) || 0;

  // Count unique assets
  const uniqueAssets = new Set<string>();
  wallets?.forEach(w => w.assets.forEach(a => uniqueAssets.add(a.asset)));

  // Count staking accounts
  const stakingCount = wallets?.filter(w => w.isStaking).length || 0;

  return {
    wallets: wallets || [],
    isLoading,
    error,
    refetch,
    adaPrice,
    // Aggregated metrics
    totalBalance,
    totalBalanceUsd,
    totalStaked,
    totalStakedUsd,
    totalWithdrawable,
    totalWithdrawableUsd,
    totalRewards,
    totalRewardsUsd,
    totalAssets: uniqueAssets.size,
    stakingCount,
  };
};
